import { useEffect, useRef } from 'react';

import { cn } from '@rocco/utils/cn';
import { usePrefersReducedMotion } from './use-prefers-reduced-motion';

/**
 * How much slower than the page the mark travels. Below 1 it lags the scroll,
 * which is what reads as depth — it sits behind the page rather than on it.
 */
const PARALLAX_RATE = 0.35;

/**
 * How far it may drift before it holds. The band it sits in is finite, and an
 * unbounded drift would carry the mark down into the sections below, where it
 * would sit behind copy it was never composed against.
 */
const PARALLAX_MAX = 260;

/**
 * A name, very large and very soft, hung from the top of the band under a
 * detail page's hero: the practice on project detail, the supplier on
 * product detail.
 *
 * The blur is what keeps it from being read as a heading; it is a texture in
 * the surface. Centred on the page, 6rem above the band's top, and drifting
 * down at a third of the scroll until it holds. The parent must be
 * positioned, since the mark is absolute against it and measures its drift
 * from it; a zero-height anchor at the band's top will do.
 *
 * `className` sets how it layers and how strong it reads: project detail
 * leaves it black behind a dark band, product detail lifts it over its band's
 * painted sections and turns it right down.
 *
 * The transform is written straight to the node inside a rAF rather than held
 * in state: this runs on every scroll frame, and a re-render per frame would
 * cost far more than the paint.
 */
export const DetailBackdropMark = ({
    name,
    className,
}: {
    name: string | null | undefined;
    className?: string;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const reduced = usePrefersReducedMotion();

    useEffect(() => {
        const el = ref.current;
        if (!el || reduced) return;

        /**
         * Where the band starts, as an offset down the document.
         *
         * Held rather than read inside the frame: the drift is a function of
         * the scroll alone, and a `getBoundingClientRect()` per rAF forces the
         * browser to lay the page out again on every scroll frame of two
         * detail pages. Re-measured whenever the page is reflowed under it.
         */
        let bandTop = 0;
        const measure = () => {
            const parent = el.parentElement;
            bandTop = parent
                ? parent.getBoundingClientRect().top + window.scrollY
                : 0;
        };

        let frame = 0;
        const apply = () => {
            frame = 0;
            // Measured from the band's own position, not the document's: the
            // drift should start when the band arrives, not carry whatever the
            // page had already scrolled before it came into view.
            const top = bandTop - window.scrollY;
            const travelled = Math.max(0, window.innerHeight - top);
            const drift = Math.min(travelled * PARALLAX_RATE, PARALLAX_MAX);
            // The -50% carries the horizontal centring: this transform is
            // written on every frame, so a `-translate-x-1/2` class would be
            // overwritten the first time the reader scrolled.
            el.style.transform = `translate3d(-50%, ${drift}px, 0)`;
        };
        const onScroll = () => {
            if (frame) return;
            frame = requestAnimationFrame(apply);
        };
        const onReflow = () => {
            measure();
            onScroll();
        };

        measure();
        apply();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onReflow);
        // The band's offset moves when anything above it changes height —
        // a hero image arriving, a section opening — and the mark would then
        // drift from a position the page no longer has.
        const observer = new ResizeObserver(onReflow);
        observer.observe(document.documentElement);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onReflow);
            observer.disconnect();
            if (frame) cancelAnimationFrame(frame);
        };
    }, [name, reduced]);

    if (!name) return null;

    return (
        <div
            ref={ref}
            aria-hidden="true"
            className={cn(
                'pointer-events-none absolute z-0 will-change-transform max-md:hidden',
                // Centred on the page. The X half of the centring lives in the
                // transform, not here — see the rAF above.
                'top-[-6rem] left-1/2 w-[92vw]',
                className,
            )}
            // The initial position, before the first scroll frame writes one.
            style={{ transform: 'translate3d(-50%, 0, 0)' }}
        >
            <p className="m-0 text-center font-sans leading-[0.9] font-medium tracking-[-0.03em] text-black uppercase blur-[10px] md:text-[9rem]">
                {name}
            </p>
        </div>
    );
};
